import * as TooltipPrimitive from "@radix-ui/react-tooltip"
import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"

import { cn } from "@/lib/utils"

/*
   Icon-only control. Enforces two things a bare <button> won't:

   Label is required — used as both the accessible name and tooltip text.
   No way to ship an unlabelled icon button from here (the failure mode
   every icon-only control has).

   44px hit area via ::after, not padding. Visible control is 32px; a
   pseudo-element extends the touch target without changing layout.
   Padding the element itself would push its neighbours apart.

   Use for: drawer toggles, zoom controls, row overflow menus, close
   buttons. Not for primary actions.
*/

export const iconButtonVariants = cva(
  [
    "relative inline-grid place-items-center shrink-0",
    "rounded-md border",
    "transition-colors duration-120 ease-out",
    "[&_svg]:shrink-0 [&_svg]:pointer-events-none",
    // 44px hit area, centred, layout-neutral
    "after:absolute after:left-1/2 after:top-1/2",
    "after:size-11 after:-translate-x-1/2 after:-translate-y-1/2",
    "after:content-['']",
    "disabled:cursor-not-allowed disabled:bg-surface-sunken",
    "disabled:text-text-disabled disabled:border-border-soft",
    // the hit area must not swallow clicks meant for neighbours when disabled
    "disabled:after:hidden",
  ],
  {
    variants: {
      variant: {
        default: [
          "bg-surface border-border text-text-2",
          "hover:bg-surface-sunken hover:border-border-strong hover:text-text",
        ],
        ghost: [
          "bg-transparent border-transparent text-text-2",
          "hover:bg-surface-sunken hover:text-text",
        ],
        destructive: ["bg-surface border-err text-err", "hover:bg-err-tint"],
      },
      size: {
        sm: "size-7 [&_svg]:size-3.5",
        md: "size-8 [&_svg]:size-4",
      },
    },
    defaultVariants: { variant: "default", size: "md" },
  },
)

export interface IconButtonProps
  extends Omit<React.ComponentPropsWithoutRef<"button">, "aria-label">,
    VariantProps<typeof iconButtonVariants> {
  /** Verb phrase: "Open messages", "Zoom in", "Close". Accessible name and tooltip. */
  label: string
  /** A lucide icon. Sized by the variant; don't set size on it yourself. */
  icon: React.ReactNode
  /** Unread/pending pip on the top-right corner. Above 99 renders "99+". Announced in the accessible name. */
  count?: number
  /** Default: "unread". */
  countLabel?: string
  tooltipSide?: "top" | "right" | "bottom" | "left"
  /** Suppress the tooltip when the control already sits inside one. */
  noTooltip?: boolean
}

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  {
    className,
    variant,
    size,
    label,
    icon,
    count,
    countLabel = "unread",
    tooltipSide = "bottom",
    noTooltip = false,
    type = "button",
    ...props
  },
  ref,
) {
  const showCount = typeof count === "number" && count > 0
  const accessibleName = showCount ? `${label}, ${count} ${countLabel}` : label

  const button = (
    <button
      ref={ref}
      type={type}
      aria-label={accessibleName}
      className={cn(iconButtonVariants({ variant, size }), className)}
      {...props}
    >
      {icon}
      {showCount && (
        <span
          aria-hidden="true"
          className={cn(
            "absolute -right-1.5 -top-1.5",
            "grid min-w-4 place-items-center rounded-full px-1",
            "border-2 border-surface bg-err",
            "font-id text-micro leading-none text-white tracking-normal",
            "h-4",
          )}
        >
          {count > 99 ? "99+" : count}
        </span>
      )}
    </button>
  )

  if (noTooltip) return button

  return (
    <TooltipPrimitive.Root>
      <TooltipPrimitive.Trigger asChild>{button}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={tooltipSide}
          sideOffset={6}
          className={cn(
            "z-toast rounded-sm px-2 py-1",
            "bg-text text-caption text-[color:var(--surface)]",
            "shadow-e3",
            "data-[state=delayed-open]:animate-in data-[state=closed]:animate-out",
            "data-[state=delayed-open]:fade-in-0 data-[state=closed]:fade-out-0",
            "duration-120 motion-reduce:animate-none",
          )}
        >
          {label}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  )
})

/**
 * Mount once, near the root. Radix requires a provider for tooltips.
 *
 * The 500ms delay is deliberate: reviewers move fast through the top bar
 * and a 200ms tooltip fires on every pass. `skipDelayDuration` keeps
 * subsequent tooltips instant once the first has opened.
 */
export const TooltipProvider = ({
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) => (
  <TooltipPrimitive.Provider delayDuration={500} skipDelayDuration={300} {...props}>
    {children}
  </TooltipPrimitive.Provider>
)
